/**
 * Input Value dialog — Access `frmReportCashFlowQryInputAdd`.
 *
 * Opened by btn1 ("Input Value") on the 4 Input pages. Pick a Quality (combo,
 * hidden for "Others"), then enter a value per week. Save upserts one row per
 * week into `report_cashflow_input_value` keyed by (input_type, woy, quality).
 * `price_from_g2` is a computed read-only display (DLookup in Access).
 */
import { Autocomplete, Box, Dialog, DialogContent, TextField, Typography } from '@mui/material'
import DialogTitleBar from '../../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import {
  useGetCashflowLookupQuery,
  useGetCashflowPriceFromG2Query,
  useSaveCashflowInputMutation,
  type WeekColumn,
} from '../../../../store/api/reportCashflowApi'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { compactDialogContentSx, formatInteger, formatStandard, numericInputProps, parseNumeric, readonlyFieldSx } from './formHelpers'

interface Props {
  open: boolean
  inputType: string // page type for Input pages; the selected row's input_type for Others
  /** Others page → hide the Quality combo (rows have no quality). */
  isOthers: boolean
  /** Others page → the selected row's Input Type name (shown as a readonly label). */
  inputName?: string | null
  pageLabel: string
  weeks: WeekColumn[]
  selectedQuality: string | null
  seedRow?: Record<string, unknown> | null
  canEdit: boolean
  onClose: () => void
}

export default function InputValueDialog({
  open,
  inputType,
  isOthers,
  inputName,
  pageLabel,
  weeks,
  selectedQuality,
  seedRow,
  canEdit,
  onClose,
}: Props) {
  const snack = useSnackbar()
  const [quality, setQuality] = useState<{ value: string; label: string; description?: string | null } | null>(null)
  const [qInput, setQInput] = useState('')
  const [values, setValues] = useState<Record<string, string>>({})
  const [save, { isLoading }] = useSaveCashflowInputMutation()

  const { data: qualityLookup } = useGetCashflowLookupQuery(
    { kind: 'quality', q: qInput || undefined },
    { skip: isOthers || !open },
  )
  const options = useMemo(() => {
    const items = qualityLookup?.items ?? []
    // Inject the seeded quality so it shows even if outside the limited lookup.
    if (selectedQuality && !items.some((i) => i.value === selectedQuality)) {
      return [{ value: selectedQuality, label: selectedQuality }, ...items]
    }
    return items
  }, [qualityLookup, selectedQuality])

  // Hydrate on open: seed quality + week cells from the selected datasheet row.
  useEffect(() => {
    if (!open) return
    // Seed description straight from the datasheet row (it already carries it) so
    // Edit shows it immediately, without waiting on the quality lookup.
    const seedDesc = (seedRow?.description as string | null | undefined) ?? null
    setQuality(
      selectedQuality
        ? { value: selectedQuality, label: selectedQuality, description: seedDesc }
        : null,
    )
    const seeded: Record<string, string> = {}
    for (const w of weeks) {
      const v = seedRow ? (seedRow as Record<string, unknown>)[w.weekCode] : null
      seeded[w.woy] = formatInteger(v as number | null)
    }
    setValues(seeded)
    setQInput('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Access ControlSource: =DLookUp(price_from_g2, v_changecost_quality_current,
  // code=[quality_code]) — live by the currently chosen quality (not the row).
  // `currentData` (not `data`) → scoped to the current quality arg; it is
  // undefined while skipped or refetching a new quality, so the field never
  // shows the previously-fetched quality's price when the form reopens.
  const { currentData: priceData } = useGetCashflowPriceFromG2Query(quality?.value ?? '', {
    skip: isOthers || !open || !quality?.value,
  })
  const priceFromG2 = priceData?.priceFromG2 ?? null

  // Description loads by the selected Code — prefer the option's description
  // (enriches a seeded code once the lookup resolves), else parse from label.
  const qualityDescription = useMemo(() => {
    if (!quality) return ''
    const fromOpt = options.find((o) => o.value === quality.value)?.description
    if (fromOpt != null) return fromOpt
    if (quality.description != null) return quality.description
    const prefix = `${quality.value} - `
    return quality.label.startsWith(prefix) ? quality.label.slice(prefix.length) : ''
  }, [quality, options])

  const handleSave = async () => {
    if (!canEdit) return
    if (!isOthers && !quality?.value) {
      window.alert('Please choose Quality!')
      return
    }
    const payloadValues: Record<string, number | null> = {}
    for (const w of weeks) {
      payloadValues[w.woy] = parseNumeric(values[w.woy])
    }
    try {
      await save({
        inputType,
        qualityCode: isOthers ? '' : quality?.value ?? '',
        values: payloadValues,
      }).unwrap()
      snack.success('Successfully Added')
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not save the input values.')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleBar
        title={`Cashflow - ${pageLabel.replace('Input - ', '')}`}
        onSave={handleSave}
        onCancel={onClose}
        saving={isLoading}
        saveDisabled={isLoading || !canEdit}
        saveLabel="Save"
      />
      <DialogContent sx={compactDialogContentSx}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5, mt: 0.5 }}>
          {isOthers && (
            <TextField
              label="Input Type"
              value={inputName ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ gridColumn: '1 / -1', ...readonlyFieldSx }}
            />
          )}
          {!isOthers && (
            <Autocomplete
              size="small"
              sx={{ gridColumn: { xs: '1 / -1', sm: 'span 3' } }}
              options={options}
              filterOptions={(x) => x}
              getOptionLabel={(o) => o.value}
              renderOption={(props, o) => (
                <li {...props} key={o.value}>
                  {o.value}
                </li>
              )}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={quality}
              onChange={(_, v) => setQuality(v)}
              inputValue={qInput}
              onInputChange={(_, v) => setQInput(v)}
              renderInput={(p) => <TextField {...p} label="Quality Code" required InputLabelProps={{ shrink: true }} />}
            />
          )}
          {!isOthers && (
            <TextField
              label="Price vs G2"
              size="small"
              value={formatStandard(priceFromG2)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={numericInputProps}
              sx={{ gridColumn: { xs: '1 / -1', sm: 'span 1' }, ...readonlyFieldSx }}
            />
          )}
          {!isOthers && (
            <TextField
              label="Description"
              size="small"
              value={qualityDescription}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ gridColumn: '1 / -1', ...readonlyFieldSx }}
            />
          )}
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
          {isOthers ? 'Input Projected Amount (USD)' : 'Input Projected Quantity (KG)'}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 1.25 }}>
          {weeks.map((w) => (
            <TextField
              key={w.woy}
              label={w.weekName}
              size="small"
              type="text"
              value={values[w.woy] ?? ''}
              onChange={(e) => setValues((prev) => ({ ...prev, [w.woy]: e.target.value }))}
              onBlur={() =>
                setValues((prev) => {
                  const f = formatInteger(prev[w.woy])
                  return f === prev[w.woy] ? prev : { ...prev, [w.woy]: f }
                })
              }
              InputLabelProps={{ shrink: true }}
              inputProps={numericInputProps}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
